/* eslint-disable react/no-array-index-key */
import { Box } from "@mui/material";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import pluralize from "pluralize";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { OTHER_LABEL } from "config/LabelMapping";
import { InformativeModal } from "new-components/modals/InformativeModal";

import { contentToolTipUnclear } from "../../LineChartMetrics/ContentStrings";

import { detectorMetricsStyle, StyledDivCell, UnclearHeaderComponent } from "./DetectorMetricsTableCells";
import { DetectorTableCell } from "./types";

export type DetectorTableRowProps = {
  label: string;
  className?: string;
  textClass?: string;
  maxCount?: number;
  midCellCount?: number;
} & (
  | {
      cells: DetectorTableCell[];
    }
  | {
      cell: DetectorTableCell;
    }
  );

export type DetectorMetricsTableRowData = DetectorTableRowProps;

type DetectorMetricsTableProps = {
  columns: string[];
  rowsData: DetectorMetricsTableRowData[];
  maxCount?: number;
  showBottomBorder?: boolean;
};

interface GridRowData {
  id: number;
  label: string;
  [key: string]: any;
}

export function DetectorMetricsTable(props: DetectorMetricsTableProps) {
  const { columns, rowsData, maxCount, showBottomBorder = true } = props;
  const [isTooltipModalOpen, setIsTooltipModalOpen] = useState(false);

  const [isFullScrollable, setIsFullScrollable] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)"); // Adjust breakpoint to match "md"
    const handleMediaChange = () => { setIsFullScrollable(mediaQuery.matches) };
    mediaQuery.addEventListener("change", handleMediaChange);
    handleMediaChange();
    return () => mediaQuery.removeEventListener("change", handleMediaChange);
  }, []);

  // Transform rowsData to DataGridPro format
  const rows: GridRowData[] = rowsData.map((row, index) => {
    const rowData: GridRowData = {
      id: index,
      label: row.label,
      className: row.className || "",
      textClass: row.textClass || "",
    };

    if ("cells" in row) {
      row.cells.forEach((cell) => {
        rowData[cell.key] = {
          value: cell.value,
          link: cell.link,
          className: cell.className,
          key: cell.key,
        };
      });
    } else if ("cell" in row) {
      rowData.TOTAL = {
        value: row.cell.value,
        link: row.cell.link,
        className: row.cell.className,
        key: row.cell.key,
      };
    }

    return rowData;
  });

  const renderCell = (params: GridRenderCellParams) => {
    const cellData = params.value as DetectorTableCell | undefined;
    if (!cellData) return null;

    const { value, link, className, key } = cellData;

    let cellClassName = className || "";
    if (
      (params.field === "0" && maxCount) ||
      key?.includes(`>${maxCount}`) ||
      (key?.includes(OTHER_LABEL) && maxCount)
    ) {
      cellClassName += " text-primary-dark";
    }

    return <StyledDivCell className={cellClassName}>{link ? <Link className="text-[20px]" to={link}>{value}</Link> : value}</StyledDivCell>;
  };

  // Create columns for DataGridPro
  const gridColumns: GridColDef[] = [
    {
      field: "label",
      headerName: "",
      width: 150,
      sortable: false,
      renderCell: (params) => <Box className="uppercase text-[14px] w-full px-1 whitespace-normal break-words">{params.value}</Box>,
    },
    ...columns.map((header) => {
      const calculatedHeaderName = (header: string) => {
        if (header === "GREATER_THAN_MAX") {
          return `> ${maxCount} ${pluralize("Object", maxCount)}`;
        }
        if (header === "NO_OBJECTS") {
          return "No objects";
        }
        if (header === "BOUNDING_BOX") {
          return "Bounding boxes";
        }
        if (header === "NO_TEXT") {
          return "No text";
        }
        if (header === "HAS_TEXT") {
          return "Has text";
        }
        return header;
      };

      const column: GridColDef = {
        field: header,
        headerName: calculatedHeaderName(header),
        flex: 1,
        minWidth: header === "UNCLEAR" && showBottomBorder ? 110 : 85,
        sortable: false,
        renderCell,
      };

      // For UNCLEAR column, use renderHeader instead of JSX in headerName
      if (header === "UNCLEAR") {
        column.renderHeader = () => (
          <UnclearHeaderComponent
            showBottomBorder={showBottomBorder}
            onTooltipClick={() => setIsTooltipModalOpen(true)}
          />
        );
      }

      return column;
    }),
  ];
  // Determine which columns should be pinned
  // Memoize pinnedColumns to ensure DataGridPro re-renders when it changes
  const pinnedColumns = useMemo(() => {
    const leftPinnedColumns = ["label"];
    if (columns.includes("0")) {
      leftPinnedColumns.push("0");
    }

    const rightPinnedColumns = columns.filter(
      (header) => header === "TOTAL" || header === "UNCLEAR" || header.includes(">") || header === OTHER_LABEL
    );

    return { left: leftPinnedColumns, right: rightPinnedColumns };
  }, [columns]); // Dependencies: re-calculate when these change

  return (
    <>
      {/* This is a custom DataGridPro component, so is not standarize. */}
      <DataGridPro
        // Use a key that changes when pinnedColumns changes to force a complete re-render
        key={`datagrid-${isFullScrollable ? "scrollable" : "fixed"}-${JSON.stringify(pinnedColumns)}`}
        rows={rows}
        columns={gridColumns}
        pinnedColumns={isFullScrollable ? pinnedColumns : undefined}
        autoHeight
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        disableColumnResize
        disableDensitySelector
        disableSelectionOnClick
        hideFooter
        headerHeight={30}
        rowHeight={40}
        sx={detectorMetricsStyle(showBottomBorder)}
      />
      <InformativeModal
        isTooltipModalOpen={isTooltipModalOpen}
        handleTooltipModalClose={() => setIsTooltipModalOpen(false)}
        modalContent={contentToolTipUnclear}
        modalTitle="Unclear Answer"
      />
    </>
  );
}
